import { useState, useEffect, useMemo, useContext, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2, Search, Users, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCNPJLookup } from "@/hooks/useCNPJLookup";
import { useContatoExistente } from "@/hooks/useContatoExistente";
import { buscarClientePorCNPJ } from "@/services/clienteService";
import { normalizarCNPJ } from "@/utils/cnpj";
import { normalizePhoneRAW } from "@/utils/phone";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { formatPedidoCode } from "@/lib/formatters";
import { PainelPedidoContext } from "@/contexts/PainelPedidoContext";
import { formatarCNPJ, formatarCPF, keepOnlyDigits } from '@/utils/formatters';
import { validarCNPJ } from '@/utils/validators';
import { useInvalidateAllPedidoQueries } from "@/hooks/useInvalidateAllPedidoQueries";
import { useConverterPropostaEmItens } from "@/hooks/useConverterPropostaEmItens";
import { criarPedidosPorFamilia, type PedidoCriado } from "@/hooks/useCriarPedidosPorFamilia";
import { buscarEValidarLeadParaPedido } from "@/lib/validarLeadParaPedido";
import { propostaPrecisaDivisao } from "@/lib/familiasProduto";
import { canHaveOwnSales, hasFullAccess, canBeConsultorPedido } from "@/lib/roles-permissions";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import type { ComposicaoProposta } from "@/constants/planosCNPJ";
// Interface para o lead completo com proposta
interface LeadCompleto {
  id: string;
  tipo_pessoa?: 'PF' | 'PJ';
  cpf?: string | null;
  proposta_composicao_cnpj?: ComposicaoProposta | null;
  proposta_fechada?: boolean;
  pedido_id?: string | null;
  // Dados do contato real do lead (texto, não ID)
  contato_real_nome?: string | null;
  contato_real_telefone?: string | null;
  contato_real_cargo?: string | null;
  contato_real_email?: string | null;
  // Dados do dono do lead (consultor/equipe)
  consultor_id?: string | null;
  equipe_id?: string | null;
}

interface AbrirPedidoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cnpjInicial?: string;
  empresaId?: string;
  observacoesIniciais?: string;
  whatsappInicial?: string;
  origemLead?: string;
  pendingTabulacaoId?: string | null;
  leadCompleto?: LeadCompleto | null; //  NOVO: Lead completo com proposta
  onSuccess?: (pedidoId: string) => void;
}

export function AbrirPedidoModal({ 
  open, 
  onOpenChange, 
  cnpjInicial, 
  empresaId, 
  observacoesIniciais, 
  whatsappInicial, 
  origemLead, 
  pendingTabulacaoId,
  leadCompleto,
  onSuccess 
}: AbrirPedidoModalProps) {
  // AVISO, NAO TRAVA (Douglas 03/09/2026): "ate quando for criar um pedido, saindo
  // de proposta para pedido, pelo menos um aviso... nao trava... mas avisa".
  // O botao Criar Pedido continua habilitado — quem decide e' o consultor.
  const isPessoaFisica = leadCompleto?.tipo_pessoa === 'PF';
  const [situacaoReceita, setSituacaoReceita] = useState<string | null>(null);
  const [step, setStep] = useState<'consultor' | 'cnpj' | 'empresa' | 'pedido'>('cnpj');
  const [cnpj, setCnpj] = useState(cnpjInicial || '');
  const [empresaSelecionada, setEmpresaSelecionada] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [erroClientePf, setErroClientePf] = useState<string | null>(null);
  const [clienteIdSelecionado, setClienteIdSelecionado] = useState<string | null>(null);

  // ⚠️ Tem de vir DEPOIS de empresaSelecionada: o array de dependencias e' avaliado
  // na render, nao dentro do callback — acima da declaracao isto quebra a tela com
  // ReferenceError, e nem o lint nem o tsc pegam.
  //
  // Um useEffect so' cobre os CINCO setStep('pedido') do arquivo. Amarrar em cada
  // um deles seria esquecer o do cliente cadastrado na hora.
  useEffect(() => {
    if (step !== 'pedido' || !empresaSelecionada?.cnpj) { setSituacaoReceita(null); return; }
    let vivo = true;
    supabase
      .from('clientes')
      .select('situacao_cadastral')
      .eq('cnpj', normalizarCNPJ(empresaSelecionada.cnpj))
      .maybeSingle()
      .then(({ data }) => { if (vivo) setSituacaoReceita((data as any)?.situacao_cadastral ?? null); });
    return () => { vivo = false; };
  }, [step, empresaSelecionada?.cnpj]);
  
  // ✅ NOVO: Seleção de consultor para gestores
  const [consultorSelecionadoId, setConsultorSelecionadoId] = useState<string>('');
  const [equipeSelecionadaId, setEquipeSelecionadaId] = useState<string | null>(null);
  
  // ✅ NOVO: Ref para bloqueio imediato de cliques múltiplos
  const isCreatingRef = useRef(false);
  
  const { toast } = useToast();
  const { user, profile } = useAuth();
  const { consultarCNPJ } = useCNPJLookup();
  const { buscarContatoPorCNPJ } = useContatoExistente();
  const painelContext = useContext(PainelPedidoContext);
  const queryClient = useQueryClient();
  const { invalidateAll } = useInvalidateAllPedidoQueries();
  const { converter } = useConverterPropostaEmItens();
  
  // ✅ NOVO: Verificar se usuário é vendedor (pode criar pedido em nome próprio)
  const isVendedor = canHaveOwnSales(profile?.role);
  
  // ✅ NOVO: Buscar consultores para seleção (só se não for vendedor)
  const { data: consultores, isLoading: isLoadingConsultores } = useQuery({
    queryKey: ['consultores-ativos-modal'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, role, equipe_id, equipes(nome)')
        .eq('ativo', true)
        .in('role', ['consultor'])
        .order('nome');
      
      if (error) throw error;
      return data || [];
    },
    enabled: open && !isVendedor,
  });
  
  // ✅ NOVO: Opções para combobox de consultores
  const opcoesConsultores: ComboboxOption[] = useMemo(() => {
    return (consultores || []).map(c => ({
      value: c.id,
      label: c.nome,
      sublabel: (c.equipes as any)?.nome || c.role,
    }));
  }, [consultores]);

  useEffect(() => {
    if (open) {
      console.log('📋 [AbrirPedidoModal] Modal aberto com props:', {
        cnpjInicial,
        empresaId,
        origemLead,
        observacoesIniciais,
        pendingTabulacaoId,
        isVendedor,
        leadConsultorId: leadCompleto?.consultor_id,
        leadEquipeId: leadCompleto?.equipe_id,
      });
      
      if (isPessoaFisica && leadCompleto?.id) {
        let ativo = true;
        setEmpresaSelecionada(null);
        setErroClientePf(null);
        setStep('pedido');
        setLoading(true);
        isCreatingRef.current = false;
        void (async () => {
          try {
            const { data: atual, error } = await supabase.from('leads_carteira')
              .select('cliente_id, cpf, tipo_pessoa, consultor_id')
              .eq('id', leadCompleto.id).single();
            if (error) throw error;
            if (atual.tipo_pessoa !== 'PF' || !atual.cliente_id) throw new Error('Lead CPF sem cliente vinculado. Revise o cadastro do lead.');
            const { data: cliente, error: erroCliente } = await supabase.from('clientes')
              .select('*').eq('id', atual.cliente_id).single();
            if (erroCliente) throw erroCliente;
            if (cliente.tipo_pessoa !== 'PF' || cliente.cpf !== atual.cpf) throw new Error('O documento do cliente difere do lead.');
            const { data: dono, error: erroDono } = await supabase.from('usuarios')
              .select('equipe_id').eq('id', atual.consultor_id).single();
            if (erroDono) throw erroDono;
            if (ativo) {
              setEmpresaSelecionada(cliente);
              setClienteIdSelecionado(cliente.id);
              setConsultorSelecionadoId(atual.consultor_id);
              setEquipeSelecionadaId(dono.equipe_id);
            }
          } catch (error) {
            if (ativo) {
              const mensagem = error instanceof Error ? error.message : 'Tente novamente.';
              setErroClientePf(mensagem);
              toast({ title: 'Não foi possível abrir o pedido CPF', description: mensagem, variant: 'destructive' });
            }
          } finally { if (ativo) setLoading(false); }
        })();
        return () => { ativo = false; };
      }

      // Reset ref de criação
      isCreatingRef.current = false;
      
      // Reset seleção de consultor
      setConsultorSelecionadoId('');
      setEquipeSelecionadaId(null);
      
      // ✅ NOVO: Se lead tem dono, usar automaticamente e pular seleção de consultor
      if (leadCompleto?.consultor_id) {
        console.log('[AbrirPedidoModal] Lead com consultor, usando automaticamente:', leadCompleto.consultor_id);
        setConsultorSelecionadoId(leadCompleto.consultor_id);

        // Regra: sempre usar a equipe ATUAL do consultor (nunca a do lead, que pode estar defasada)
        if (leadCompleto.equipe_id) {
          setEquipeSelecionadaId(leadCompleto.equipe_id);
        } else {
          // Lead sem equipe_id → buscar equipe real do consultor no banco
          supabase
            .from('usuarios')
            .select('equipe_id')
            .eq('id', leadCompleto.consultor_id)
            .maybeSingle()
            .then(({ data }) => {
              setEquipeSelecionadaId(data?.equipe_id || null);
              console.log('[AbrirPedidoModal] Equipe buscada do consultor:', data?.equipe_id);
            });
        }
        
        // Pular direto para CNPJ ou buscar empresa
        if (empresaId) {
          buscarEmpresaPorId(empresaId);
        } else if (cnpjInicial) {
          setCnpj(cnpjInicial);
          setStep('cnpj');
          setTimeout(() => buscarEmpresa(cnpjInicial), 100);
        } else {
          setStep('cnpj');
        }
        setEmpresaSelecionada(null);
        return;
      }
      
      // Fluxo existente para leads SEM dono ou criação manual
      if (empresaId) {
        // Se empresaId foi passado, buscar empresa e ir direto para seleção de produtos
        buscarEmpresaPorId(empresaId);
      } else if (cnpjInicial) {
        // Se tem CNPJ inicial, ir direto para CNPJ (vendedor) ou consultor (gestor)
        setCnpj(cnpjInicial);
        if (isVendedor) {
          setStep('cnpj');
          setTimeout(() => buscarEmpresa(cnpjInicial), 100);
        } else {
          setStep('consultor');
        }
      } else {
        // Fluxo normal
        if (isVendedor) {
          // Vendedor vai direto para CNPJ
          setStep('cnpj');
        } else {
          // Gestor/BKO precisa escolher consultor primeiro
          setStep('consultor');
        }
        setCnpj('');
      }
      setEmpresaSelecionada(null);
    }
    // ⚠️ Depender dos CAMPOS primitivos do leadCompleto, NUNCA do objeto inteiro:
    // o pai passa leadCompleto como referência nova a cada render → o effect
    // re-rodava todo render (setStep/setCnpj/buscarEmpresa) → loop infinito =
    // modal "piscando" e sem conseguir enviar o CNPJ.
  }, [open, cnpjInicial, empresaId, origemLead, isVendedor, leadCompleto?.consultor_id, leadCompleto?.equipe_id, isPessoaFisica, leadCompleto?.id]);

  const handleClose = () => {
    onOpenChange(false);
    setStep('cnpj');
    setCnpj('');
    setEmpresaSelecionada(null);
    setConsultorSelecionadoId('');
    setEquipeSelecionadaId(null);
    isCreatingRef.current = false;
  };
  
  // ✅ NOVO: Handler para seleção de consultor
  const handleSelecionarConsultor = (consultorId: string) => {
    setConsultorSelecionadoId(consultorId);
    
    // Buscar equipe do consultor selecionado
    const consultor = consultores?.find(c => c.id === consultorId);
    setEquipeSelecionadaId(consultor?.equipe_id || null);
    
    // Avançar para step de CNPJ
    if (cnpjInicial) {
      setCnpj(cnpjInicial);
      setTimeout(() => buscarEmpresa(cnpjInicial), 100);
    } else {
      setStep('cnpj');
    }
  };

  const buscarEmpresaPorId = async (id: string) => {
    setLoading(true);
    try {
      const { data: empresa, error } = await supabase
        .from('empresa')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      setEmpresaSelecionada(empresa);
      setStep('pedido');
    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      toast({
        title: "Erro",
        description: "Empresa não encontrada",
        variant: "destructive"
      });
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const buscarEmpresa = async (cnpjParaBuscar?: string) => {
    const cnpjAUsar = cnpjParaBuscar || cnpj;
    
    // Guard contra cliques duplicados
    if (loading) {
      console.warn('[AbrirPedidoModal] Busca já em andamento, ignorando');
      return;
    }
    
    if (!validarCNPJ(cnpjAUsar)) {
      toast({
        title: "CNPJ inválido",
        description: "Por favor, digite um CNPJ válido com 14 dígitos.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    const cnpjLimpo = cnpjAUsar.replace(/\D/g, '');

    try {
      // Primeiro, buscar na base local
      const { data: empresaExistente } = await supabase
        .from('empresa')
        .select('*')
        .eq('cnpj', cnpjLimpo)
        .single();

      if (empresaExistente) {
        setEmpresaSelecionada(empresaExistente);
        setStep('pedido');
        return;
      }

      // ✅ FALLBACK: Buscar na tabela 'clientes' (hub central) antes de chamar API externa
      console.log('[AbrirPedidoModal] Empresa não encontrada, buscando em clientes...');
      const { data: clienteExistente } = await supabase
        .from('clientes')
        .select('cnpj, razao_social, nome_fantasia, telefone_principal, email, endereco, whatsapp')
        .eq('cnpj', cnpjLimpo)
        .maybeSingle();

      if (clienteExistente) {
        console.log('[AbrirPedidoModal] ✅ Cliente encontrado na tabela clientes:', clienteExistente.razao_social);
        const novaEmpresaFromCliente = {
          cnpj: cnpjLimpo,
          razao_social: clienteExistente.razao_social,
          nome_fantasia: clienteExistente.nome_fantasia,
          telefone: clienteExistente.telefone_principal,
          email: clienteExistente.email,
          endereco: clienteExistente.endereco ? { logradouro: clienteExistente.endereco } : {},
          origem: 'manual'
        };
        const { data: empresaCriadaFromCliente, error: errCliente } = await supabase
          .from('empresa')
          .insert(novaEmpresaFromCliente)
          .select()
          .single();

        if (empresaCriadaFromCliente && !errCliente) {
          setEmpresaSelecionada(empresaCriadaFromCliente);
          setStep('pedido');
          toast({
            title: "Empresa encontrada",
            description: `Dados importados do cadastro de clientes: ${clienteExistente.razao_social}`
          });
          return;
        }
        console.warn('[AbrirPedidoModal] Erro ao criar empresa a partir de clientes:', errCliente);
      }

      // Se não encontrou em nenhuma tabela local, tentar buscar via API da Receita
      try {
        const resultado = await consultarCNPJ(cnpjLimpo);
        const notFoundMarker = resultado && (resultado as any).not_found;
        if (!resultado || notFoundMarker) {
          // API não encontrou — mostrar formulário manual
          setStep('empresa');
          return;
        }
        const dadosCNPJ = resultado.data;
        
        if (dadosCNPJ) {
          // Criar empresa a partir dos dados da Receita
          const novaEmpresa = {
            cnpj: cnpjLimpo,
            razao_social: dadosCNPJ.razao_social,
            nome_fantasia: dadosCNPJ.nome_fantasia,
            telefone: dadosCNPJ.telefone_principal,
            email: dadosCNPJ.email_principal,
            endereco: {
              logradouro: dadosCNPJ.endereco_rua || '',
              numero: dadosCNPJ.endereco_numero || '',
              bairro: dadosCNPJ.endereco_bairro || '',
              cidade: dadosCNPJ.endereco_cidade || '',
              uf: dadosCNPJ.endereco_uf || '',
              cep: dadosCNPJ.endereco_cep || ''
            },
            origem: 'api_receita'
          };

          const { data: empresaCriada, error } = await supabase
            .from('empresa')
            .insert(novaEmpresa)
            .select()
            .single();

          if (error) throw error;

          setEmpresaSelecionada(empresaCriada);
          setStep('pedido');
          
          toast({
            title: "Empresa encontrada",
            description: "Dados importados da Receita Federal com sucesso!"
          });
        } else {
          // Se não conseguiu buscar na Receita, mostrar formulário manual
          setStep('empresa');
        }
      } catch (apiError) {
        // Erro na API ou token não configurado, mostrar formulário manual
        setStep('empresa');
      }

    } catch (error) {
      console.error('Erro ao buscar empresa:', error);
      toast({
        title: "Erro",
        description: "Erro ao buscar empresa. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const criarEmpresaManual = async (dadosEmpresa: any) => {
    setLoading(true);
    
    try {
      const novaEmpresa = {
        cnpj: cnpj.replace(/\D/g, ''),
        razao_social: dadosEmpresa.razaoSocial,
        nome_fantasia: dadosEmpresa.nomeFantasia,
        telefone: dadosEmpresa.telefone,
        email: dadosEmpresa.email,
        endereco: dadosEmpresa.endereco || {},
        origem: 'manual'
      };

      const { data: empresaCriada, error } = await supabase
        .from('empresa')
        .insert(novaEmpresa)
        .select()
        .single();

      if (error) throw error;

      setEmpresaSelecionada(empresaCriada);
      setStep('pedido');
      
      toast({
        title: "Empresa criada",
        description: "Empresa cadastrada com sucesso!"
      });
    } catch (error) {
      console.error('Erro ao criar empresa:', error);
      toast({
        title: "Erro",
        description: "Erro ao cadastrar empresa. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Helper: Criar ou buscar gestor a partir dos dados do lead ou do CRM
  const criarOuBuscarGestor = async (
    clienteId: string,
    lead: LeadCompleto | null,
    cnpj?: string
  ): Promise<string | null> => {
    let nome = lead?.contato_real_nome;
    let telefone = lead?.contato_real_telefone;
    let email = lead?.contato_real_email;
    let cargo = lead?.contato_real_cargo;

    // ✅ REGRA MASTER: Se lead não tem contato, buscar no CRM pelo CNPJ
    if (!nome && cnpj) {
      console.log('[AbrirPedidoModal] Lead sem contato, buscando no CRM...');
      const contatoExistente = await buscarContatoPorCNPJ(cnpj);
      
      if (contatoExistente) {
        console.log('[AbrirPedidoModal] Contato encontrado no CRM:', contatoExistente.nome);
        nome = contatoExistente.nome;
        telefone = contatoExistente.telefone;
        email = contatoExistente.email;
        cargo = contatoExistente.cargo;
      }
    }

    if (!nome) return null;

    // Verificar se já existe contato com esse nome para esse cliente
    const { data: existente } = await supabase
      .from('cliente_contato')
      .select('id')
      .eq('cliente_id', clienteId)
      .eq('nome', nome)
      .maybeSingle();

    if (existente) return existente.id;

    // Criar novo contato
    const { data: novo, error } = await supabase
      .from('cliente_contato')
      .insert({
        cliente_id: clienteId,
        nome,
        telefone,
        whatsapp: telefone,
        email,
        cargo,
        is_principal: true,
      })
      .select('id')
      .single();

    if (error) {
      console.error('[AbrirPedidoModal] Erro ao criar gestor:', error);
      return null;
    }

    console.log('[AbrirPedidoModal] Gestor criado:', novo.id);
    return novo.id;
  };

  const criarPedido = async () => {
    // ✅ BLOQUEIO IMEDIATO - antes de qualquer validação async
    if (isCreatingRef.current) {
      // ⛔ Engolir o 2º clique em silêncio fazia a tela parecer travada: a criação
      // leva ~10 idas ao banco e nada mudava na tela nesse tempo. (Douglas 02/09/2026)
      toast({ title: 'Criando o pedido', description: 'Já está em andamento — aguarde alguns segundos.' });
      return;
    }
    isCreatingRef.current = true;
    // Spinner no botão desde o primeiro clique; o `finally` zera.
    setLoading(true);
    
    // VALIDAÇÃO 1: Empresa selecionada
    if (!empresaSelecionada) {
      toast({
        title: "Erro",
        description: "Nenhuma empresa selecionada",
        variant: "destructive"
      });
      isCreatingRef.current = false;
      setLoading(false);
      return;
    }

    // VALIDAÇÃO 2: Perfil carregado
    if (!profile?.id) {
      toast({
        title: "Erro",
        description: "Perfil de usuário não carregado. Tente novamente.",
        variant: "destructive"
      });
      isCreatingRef.current = false;
      setLoading(false);
      return;
    }
    
    // ✅ MODO FANTASMA: Roles administrativos NUNCA podem ser consultor_id
    const isRoleAdministrativo = hasFullAccess(profile?.role) || ['diretor', 'gerente'].includes(profile?.role || '');
    // CONSULTOR RETENÇÃO não tem regra de dono (Douglas 09/10/2026): pedido que
    // ele abre é dele, mesmo no lead de outro consultor — o lead passa para ele
    // antes do pedido nascer, e o dono do cliente não muda.
    const souRetencao = profile?.funcao === 'CONSULTOR_RETENCAO';
    const consultorIdFinal = (souRetencao ? profile.id : null) || leadCompleto?.consultor_id || consultorSelecionadoId || (isRoleAdministrativo ? null : profile.id);
    
    if (!consultorIdFinal) {
      toast({
        title: "Selecione um consultor",
        description: "Você precisa selecionar um consultor para o pedido.",
        variant: "destructive"
      });
      isCreatingRef.current = false;
      setLoading(false);
      return;
    }

    // ✅ VALIDAÇÃO DE ROLE: Apenas 'consultor' pode ser consultor_id de pedido
    const consultorCandidato = consultores?.find(c => c.id === consultorIdFinal)
      ?? (consultorIdFinal === profile?.id ? { role: profile?.role } : null);
    const roleCandidato = (consultorCandidato as any)?.role;
    if (roleCandidato && !canBeConsultorPedido(roleCandidato)) {
      toast({
        title: "Ação não permitida",
        description: `O perfil "${roleCandidato}" não pode ser vinculado a pedidos. Apenas consultores podem ter pedidos criados em seu nome. Selecione um consultor da equipe.`,
        variant: "warning",
        duration: 8000,
      });
      isCreatingRef.current = false;
      setLoading(false);
      return;
    }

    // ✅ VALIDAÇÃO BKO: BKO só pode criar pedidos para leads da sua equipe mapeada
    const isBKO = profile?.funcao?.includes('BKO');
    if (isBKO && leadCompleto?.equipe_id) {
      const { data: equipesPermitidas } = await supabase
        .from('bko_equipe_responsavel')
        .select('equipe_id')
        .eq('usuario_id', profile.id);

      const idsPermitidos = (equipesPermitidas || []).map(e => e.equipe_id);
      if (idsPermitidos.length > 0 && !idsPermitidos.includes(leadCompleto.equipe_id)) {
        // Buscar nome da equipe do lead para mostrar na mensagem
        const { data: equipeData } = await supabase
          .from('equipes')
          .select('nome')
          .eq('id', leadCompleto.equipe_id)
          .maybeSingle();

        const nomeEquipeLead = equipeData?.nome || 'outra equipe';
        toast({
          title: "Ação não permitida",
          description: `Você não está mapeado como BKO responsável pela equipe "${nomeEquipeLead}". Apenas o BKO responsável por esta equipe pode criar pedidos para este lead. Solicite ao gestor ou ao BKO da equipe.`,
          variant: "warning",
          duration: 8000,
        });
        isCreatingRef.current = false;
        setLoading(false);
        return;
      }
    }
    // Regra: equipe deve ser SEMPRE a do consultor selecionado, nunca a do usuário logado
    // equipeSelecionadaId já foi populado com a equipe atual do consultor (via banco)
    const equipeIdFinal = souRetencao ? (profile.equipe_id ?? equipeSelecionadaId) : (equipeSelecionadaId ?? profile.equipe_id);

    // VALIDAÇÃO 3: Idempotência via RPC SECURITY DEFINER (bypass RLS)
    if (leadCompleto?.id) {
      const { data: leadAtual, error: errIdempotencia } = await supabase
        .rpc('api_verificar_lead_convertido', { p_lead_id: leadCompleto.id })
        .single();
      
      if (errIdempotencia) {
        console.warn('Erro na verificação de idempotência:', errIdempotencia);
      }

      if (leadAtual?.proposta_fechada || leadAtual?.pedido_id) {
        toast({
          title: "Lead já convertido",
          description: "Este lead já foi convertido em pedido anteriormente. Não é possível criar um novo pedido para o mesmo lead.",
          variant: "warning"
        });
        onOpenChange(false);
        isCreatingRef.current = false;
        setLoading(false);
        return;
      }
    }

    // VALIDAÇÃO 4: Barreira final - validação centralizada do lead (contato real + Bee + duplicata)
    if (leadCompleto?.id) {
      const validacaoLead = await buscarEValidarLeadParaPedido(leadCompleto.id);

      if (!validacaoLead.valido) {
        toast({
          title: `${validacaoLead.erro || 'Ação não permitida'}`,
          description: validacaoLead.descricao,
          variant: 'warning',
          duration: 6000,
        });
        isCreatingRef.current = false;
        setLoading(false);
        return;
      }
    }

    if (souRetencao && leadCompleto?.id && leadCompleto.consultor_id && leadCompleto.consultor_id !== profile.id) {
      const { error: moverErro } = await supabase
        .from('leads_carteira')
        .update({
          consultor_id: profile.id,
          equipe_id: equipeIdFinal,
          atribuido_por: profile.id,
          data_atribuicao: new Date().toISOString(),
        })
        .eq('id', leadCompleto.id);
      const { data: conferido } = await supabase
        .from('leads_carteira')
        .select('consultor_id')
        .eq('id', leadCompleto.id)
        .maybeSingle();
      if (moverErro || (conferido as { consultor_id?: string | null } | null)?.consultor_id !== profile.id) {
        toast({
          title: 'O lead não passou para o Consultor Retenção',
          description: moverErro?.message || 'O pedido não foi criado para não nascer no nome do dono. Avise a gestão com o número do lead.',
          variant: 'destructive',
          duration: 8000,
        });
        isCreatingRef.current = false;
        setLoading(false);
        return;
      }
      await supabase.from('leads_carteira_historico').insert({
        lead_id: leadCompleto.id,
        acao: 'TRANSFERIDO',
        usuario_id: profile.id,
        observacao: 'Lead passou para o Consultor Retenção ao abrir o pedido (retenção não tem regra de dono)',
      });
    }

    try {
      // 1. Buscar ou criar cliente baseado no CNPJ da empresa
      const cnpjNormalizado = isPessoaFisica ? '' : normalizarCNPJ(empresaSelecionada.cnpj);
      const clienteExistente = isPessoaFisica
        ? { cliente_id: empresaSelecionada.id }
        : await buscarClientePorCNPJ(cnpjNormalizado);
      
      let clienteId: string;
      
      if (clienteExistente) {
        // Cliente já existe
        clienteId = clienteExistente.cliente_id;
        setClienteIdSelecionado(clienteId);
        
        // ✅ Atualizar WhatsApp se não existir
        if (whatsappInicial) {
          // Buscar dados completos do cliente para verificar WhatsApp
          const { data: clienteCompleto } = await supabase
            .from('clientes')
            .select('whatsapp')
            .eq('id', clienteId)
            .single();
          
          if (!clienteCompleto?.whatsapp) {
            const numeroNormalizado = normalizePhoneRAW(whatsappInicial.replace(/\D/g, ''));
            await supabase
              .from('clientes')
              .update({
                telefone_principal: numeroNormalizado,
                whatsapp: numeroNormalizado
              })
              .eq('id', clienteId);
            
            toast({
              title: "WhatsApp atualizado",
              description: "Número do WhatsApp foi adicionado ao cliente"
            });
          }
        }
      } else {
        // Criar novo cliente a partir dos dados da empresa
        const numeroNormalizado = whatsappInicial 
          ? normalizePhoneRAW(whatsappInicial.replace(/\D/g, ''))
          : null;
          
        const { data: novoCliente, error: clienteError } = await supabase
          .from('clientes')
          .insert({
            cnpj: cnpjNormalizado,
            razao_social: empresaSelecionada.razao_social,
            nome_fantasia: empresaSelecionada.nome_fantasia,
            classificacao_cliente: 'Outro',
            // A consulta ja trouxe a situacao — nao grava-la fazia o cliente nascer
            // NULL e escapar da trava de campanha para sempre (o worker so preenche
            // quem esta vazio uma vez). O outro criador de cliente, o
            // clienteService.ts, ja gravava; so este ficava de fora.
            situacao_cadastral: empresaSelecionada.situacao_cadastral ?? null,
            telefone_principal: numeroNormalizado,
            whatsapp: numeroNormalizado
          })
          .select()
          .single();

        if (clienteError) throw clienteError;
        clienteId = novoCliente.id;
        setClienteIdSelecionado(clienteId);

        // Criar donos_cliente associando ao consultor (usa consultorIdFinal)
        await supabase
          .from('donos_cliente')
          .insert({
            cliente_id: clienteId,
            owner_consultor_id: consultorIdFinal
          });

        // FLUXO OFICIAL: Registrar histórico CLAIM_PEDIDO_NOVO
        await supabase.from('historico_donos_cliente').insert({
          cliente_id: clienteId,
          funcao: 'consultor',
          de_user_id: null,
          para_user_id: consultorIdFinal,
          motivo: 'CLAIM_PEDIDO_NOVO',
          tipo_movimento: 'CLAIM_PEDIDO_NOVO',
          observacao: 'Vínculo temporário criado na abertura de pedido para cliente novo',
          movido_por: profile?.id || null,
        });
      }

      // 2. Verificar se proposta precisa ser dividida por família
      let itensForamCriados = false;
      let pedidosCriados: PedidoCriado[] = [];
      let pedidoPrincipal: { id: string; numero_pedido: number } | null = null;

      // 🛡️ RAIZ DO FANTASMA (25/06): a composicao TEM que vir FRESCA do banco, nunca so do
      // prop `leadCompleto` — se a proposta foi montada DEPOIS de abrir o lead, o snapshot do
      // prop chega vazio/velho e a conversao gera 0 itens (97% dos fantasmas tinham proposta
      // com linhas salvas no banco). Re-busca por id; so cai no prop se a query falhar.
      let composicaoFinal = leadCompleto?.proposta_composicao_cnpj || null;
      if (leadCompleto?.id) {
        try {
          const { data: leadFresh } = await supabase
            .from('leads_carteira')
            .select('proposta_composicao_cnpj')
            .eq('id', leadCompleto.id)
            .maybeSingle();
          if (leadFresh && Object.prototype.hasOwnProperty.call(leadFresh, 'proposta_composicao_cnpj')) {
            composicaoFinal = (leadFresh as unknown as { proposta_composicao_cnpj?: typeof composicaoFinal }).proposta_composicao_cnpj || composicaoFinal;
          }
        } catch (e) {
          console.warn('[AbrirPedidoModal] re-fetch da composicao falhou, usando prop:', e);
        }
      }

      if (composicaoFinal) {
        console.log('📋 [AbrirPedidoModal] Convertendo proposta em itens...');
        
        // ✅ Criar/buscar gestor a partir dos dados do lead OU do CRM
        const cnpjNormalizadoParaGestor = isPessoaFisica ? undefined : normalizarCNPJ(empresaSelecionada.cnpj);
        const gestorId = await criarOuBuscarGestor(clienteId, leadCompleto, cnpjNormalizadoParaGestor);

        // ✅ NOVA LÓGICA: Criar pedidos separados por família (FIXA vs MÓVEL)
        const resultado = await criarPedidosPorFamilia({
          clienteId,
          consultorId: consultorIdFinal, //  Usa consultor selecionado (gestor) ou próprio usuário
          origemLead: origemLead || 'MANUAL',
          composicao: isPessoaFisica ? { ...composicaoFinal, tipoPessoa: 'PF' } : composicaoFinal,
          gestorId: gestorId || undefined,
          pendingTabulacaoId,
          leadId: leadCompleto.id,
        });

        if (resultado.success && resultado.totalItens > 0) {
          console.log(`✅ [AbrirPedidoModal] ${resultado.totalItens} itens criados em ${resultado.pedidos.length} pedido(s)`);
          itensForamCriados = true;
          pedidosCriados = resultado.pedidos;
          
          // Pedido principal = primeiro pedido criado (MÓVEL tem prioridade)
          const pedidoMovel = resultado.pedidos.find(p => p.familia === 'MOVEL');
          const pedidoFixa = resultado.pedidos.find(p => p.familia === 'FIXA');
          pedidoPrincipal = pedidoMovel || pedidoFixa || null;
          
          if (resultado.precisouDividir) {
            toast({
              title: "2 pedidos criados!",
              description: `Proposta dividida: FIXA (#${formatPedidoCode(pedidoFixa?.numero_pedido || 0)}) e MÓVEL (#${formatPedidoCode(pedidoMovel?.numero_pedido || 0)})`,
            });
          } else {
            toast({
              title: "Itens criados",
              description: `${resultado.totalItens} item(ns) criado(s) a partir da proposta`
            });
          }
        } else {
          console.error('⚠️ [AbrirPedidoModal] Nenhum item foi criado:', resultado.errors);
          throw new Error(resultado.errors[0] || 'A proposta não gerou itens válidos. Revise a proposta antes de criar o pedido.');
        }
      } else {
        // Fluxo manual (sem proposta): o pedido nasce VAZIO e o consultor põe os
        // itens depois, no painel. Quem abandona no meio deixa um pedido vazio
        // pendurado — e cada nova tentativa criava mais um. Amanda fez 5 no mesmo
        // minuto para o mesmo cliente em 05/08 (2608050012 a 16), Jenifer 3 em
        // 04/08. Eram 332 vazios órfãos acumulados desde 24/11/2025.
        //
        // Agora REAPROVEITA o vazio da tentativa anterior, mesmo padrão que o
        // BotaoFecharPedido já usa com o filho ("Reaproveitando filho VAZIO").
        console.log('📝 [AbrirPedidoModal] Criando pedido manual (sem proposta)...');

        let pedidoReaproveitado: { id: string; numero_pedido: number } | null = null;
        const { data: candidatos } = await supabase
          .from('pedidos')
          .select('id, numero_pedido')
          .eq('cliente_id', clienteId)
          .eq('consultor_id', consultorIdFinal)
          .eq('deletado', false)
          .is('pedido_mae_id', null)
          .gte('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString())
          .order('created_at', { ascending: false })
          .limit(5);

        for (const candidato of candidatos || []) {
          const { count } = await supabase
            .from('itens_pedido')
            .select('id', { count: 'exact', head: true })
            .eq('pedido_id', candidato.id);
          if ((count ?? 0) === 0) {
            pedidoReaproveitado = candidato as { id: string; numero_pedido: number };
            console.log('♻️ [AbrirPedidoModal] Reaproveitando pedido VAZIO da tentativa anterior:', pedidoReaproveitado.id);
            break;
          }
        }

        let { data: pedido, error: errorPedido } = pedidoReaproveitado
          ? { data: pedidoReaproveitado, error: null }
          : await supabase
          .from('pedidos')
          .insert([{
            cliente_id: clienteId,
            consultor_id: consultorIdFinal, //  Usa consultor selecionado (gestor) ou próprio usuário
            data_pedido: new Date().toISOString().split('T')[0],
            data_referencia: new Date().toISOString().split('T')[0],
            numero_pedido: 0,
            origem_lead: origemLead || 'MANUAL',
            lead_origem_id: leadCompleto?.id || null,
          }])
          .select('id, numero_pedido')
          .maybeSingle();

        // ROBUSTEZ (15/06): se o insert não devolver o id (intermitente no client),
        // re-busca o pedido recém-criado em vez de falhar com "Nenhum pedido foi criado".
        if (!errorPedido && !pedido?.id) {
          const { data: recente } = await supabase
            .from('pedidos')
            .select('id, numero_pedido')
            .eq('cliente_id', clienteId)
            .eq('consultor_id', consultorIdFinal)
            .gte('created_at', new Date(Date.now() - 20000).toISOString())
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          if (recente?.id) pedido = recente;
        }

        if (errorPedido) throw errorPedido;
        if (!pedido?.id) throw new Error('Pedido criado mas id não retornado');

        pedidoPrincipal = pedido;
        pedidosCriados = [{ id: pedido.id, numero_pedido: pedido.numero_pedido, familia: 'MOVEL' as const, itensCreados: 0 }];
        
        // Vincular tabulação se existir
        if (pendingTabulacaoId) {
          await supabase
            .from('discadora_historico_tabulacao')
            .update({ pedido_id: pedido.id })
            .eq('id', pendingTabulacaoId);
        }
        
        console.log('✅ [AbrirPedidoModal] Pedido manual criado:', pedido.id);
      }

      // Se nenhum pedido foi criado, abortar
      if (!pedidoPrincipal) {
        throw new Error('Nenhum pedido foi criado');
      }

      const pedidoId = pedidoPrincipal.id;
      const numeroPedido = pedidoPrincipal.numero_pedido;

      // ✅ CORREÇÃO CRÍTICA: Marcar lead como fechado COM TODOS OS CAMPOS necessários
      if (leadCompleto?.id) {
        // Sempre vincular lead_origem_id ao pedido (para rastreabilidade) - já foi feito no criarPedidosPorFamilia
        // mas precisa fazer para o pedido principal se ainda não foi
        if (pedidosCriados.length > 0) {
          await supabase
            .from('pedidos')
            .update({ lead_origem_id: leadCompleto.id })
            .eq('id', pedidoId);
        }
        
        const nowIso = new Date().toISOString();
        
        // ✅ Só marcar como CONVERTIDO se itens foram criados
        if (itensForamCriados) {
          console.log('📝 [AbrirPedidoModal] Marcando lead como CONVERTIDO (itens criados)...');
          
          // ✅ (C) Criar snapshot para rastreabilidade
          const snapshot = {
            composicao: JSON.parse(JSON.stringify(composicaoFinal)),
            convertido_em: nowIso,
            convertido_por: profile.id,
            valor_total: composicaoFinal?.totalValor || 0,
            total_linhas: composicaoFinal?.linhas?.length || 0,
            pedidos_criados: pedidosCriados.map(p => ({ id: p.id, numero: p.numero_pedido, familia: p.familia })),
          };
          
          // ✅ UPDATE ATÔMICO - Incluir status_lead, proposta_status e snapshot
          const { error: updateLeadError } = await supabase
            .from('leads_carteira')
            .update({ 
              proposta_fechada: true,
              proposta_status: 'CONVERTIDA',       //  (C) Status da proposta
              proposta_snapshot: snapshot,          //  (C) Snapshot para rastreabilidade
              pedido_id: pedidoId,
              cliente_id: clienteId,
              status_lead: 'CONVERTIDO',           //  CRÍTICO: Status final
              status_prospeccao: null,             //  CRÍTICO: Limpar "Negociando"
              data_conversao: nowIso,
              updated_at: nowIso
            })
            .eq('id', leadCompleto.id);

          // ✅ (C) SE FALHAR → ABORTAR (não seguir fluxo)
          if (updateLeadError) {
            console.error('❌ [AbrirPedidoModal] Erro ao atualizar lead:', updateLeadError);
            throw new Error(`Erro ao atualizar lead: ${updateLeadError.message}`);
          }
          
          console.log('✅ [AbrirPedidoModal] Lead CONVERTIDO com snapshot salvo');
          
          // Registrar no histórico
          await supabase
            .from('leads_carteira_historico')
            .insert({
              lead_id: leadCompleto.id,
              usuario_id: profile.id,
              acao: 'CONVERTIDO',
              observacao: `Lead convertido em ${pedidosCriados.length} pedido(s). IDs: ${pedidosCriados.map(p => p.id).join(', ')}`
            });

          // ✅ ATUALIZAÇÃO IMEDIATA DO CACHE - Patch síncrono para UI instantânea
          const leadPatch = {
            proposta_fechada: true,
            pedido_id: pedidoId,
            cliente_id: clienteId,
            status_lead: 'CONVERTIDO',
            status_prospeccao: null,
            data_conversao: nowIso,
            updated_at: nowIso
          };
          
          // Patch em todas as queries de leads para UI imediata
          const queryKeysToUpdate = [
            'meus-leads-carteira-optimized',
            'leads-prospeccao-server',
            'leads-prospeccao-hierarquia',
            'gestao-leads-optimized',
            'gestao-carteira-optimized',
            'gestao-carteira',
          ];
          
          queryKeysToUpdate.forEach(keyPrefix => {
            queryClient.setQueriesData(
              { queryKey: [keyPrefix], exact: false },
              (oldData: any) => {
                if (!oldData) return oldData;
                
                // Para infinite queries (pages)
                if (oldData.pages) {
                  return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                      ...page,
                      leads: page.leads?.map((lead: any) =>
                        lead.id === leadCompleto.id ? { ...lead, ...leadPatch } : lead
                      )
                    }))
                  };
                }
                
                // Para queries simples com leads array
                if (oldData.leads) {
                  return {
                    ...oldData,
                    leads: oldData.leads.map((lead: any) =>
                      lead.id === leadCompleto.id ? { ...lead, ...leadPatch } : lead
                    )
                  };
                }
                
                // Para queries com estrutura específica (meusClientes, novosLeads, etc)
                if (oldData.meusClientes || oldData.novosLeads) {
                  return {
                    ...oldData,
                    meusClientes: oldData.meusClientes?.map((lead: any) =>
                      lead.id === leadCompleto.id ? { ...lead, ...leadPatch } : lead
                    ),
                    novosLeads: oldData.novosLeads?.map((lead: any) =>
                      lead.id === leadCompleto.id ? { ...lead, ...leadPatch } : lead
                    ),
                    convertidos: [
                      ...(oldData.convertidos?.filter((l: any) => l.id !== leadCompleto.id) || []),
                      { ...(oldData.meusClientes?.find((l: any) => l.id === leadCompleto.id) || 
                            oldData.novosLeads?.find((l: any) => l.id === leadCompleto.id) || {}), ...leadPatch }
                    ],
                    leadsTabulacao: oldData.leadsTabulacao?.filter((lead: any) => lead.id !== leadCompleto.id)
                  };
                }
                
                return oldData;
              }
            );
          });
          
          console.log('✅ [AbrirPedidoModal] Cache atualizado sincronamente');
          
        } else {
          console.log('⚠️ [AbrirPedidoModal] Lead NÃO marcado como fechado (sem itens criados)');
          // Vincular pedido_id mas não marcar como proposta_fechada
          await supabase
            .from('leads_carteira')
            .update({ pedido_id: pedidoId })
            .eq('id', leadCompleto.id);
        }
      }
      
      // 4. Invalidar queries (com refetch forçado para consistência)
      await invalidateAll();
      
      // ✅ Invalidar e refetch todas as queries de leads para garantir consistência
      const leadQueryKeys = [
        ['meus-leads-carteira-optimized'],
        ['leads-prospeccao-server'],
        ['leads-prospeccao-hierarquia'],
        ['leads-prospeccao-metricas'],
        ['gestao-leads-optimized'],
        ['gestao-leads-metrics'],
        ['gestao-carteira-optimized'],
        ['gestao-carteira'],
        ['leads-disponiveis-count'],
      ];
      
      leadQueryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: key, exact: false });
      });
      
      // Refetch ativo após 100ms para garantir UI atualizada
      setTimeout(() => {
        leadQueryKeys.forEach(key => {
          queryClient.refetchQueries({ queryKey: key, exact: false, type: 'active' });
        });
      }, 100);

      const codigoPedido = formatPedidoCode(numeroPedido);
      
      // 5. Fechar modal
      onOpenChange(false);
      
      // 6. Abrir Pedido 360 diretamente (sem obrigatoriedade de item)
      if (painelContext) {
        setTimeout(() => {
          painelContext.open(pedidoId, 'planilha');
        }, 200);
      }

      // Toast com ação para abrir pedido manualmente (caso automático falhe)
      // Só mostra se não foi a mensagem de divisão
      if (!pedidosCriados.some(p => p.familia === 'FIXA') || !pedidosCriados.some(p => p.familia === 'MOVEL')) {
        toast({
          title: "✓ Pedido criado!",
          description: `${codigoPedido} - Clique para abrir`,
          action: (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => painelContext?.open(pedidoId, 'planilha')}
            >
              Abrir
            </Button>
          )
        });
      }

      // Chamar callback de sucesso se fornecido
      if (onSuccess) {
        onSuccess(pedidoId);
      }
      
    } catch (error: any) {
      console.error('❌ [AbrirPedidoModal] Erro ao criar pedido:', error);
      
      // Extrair mensagem real do erro
      let errorMessage = 'Erro ao criar pedido. Tente novamente.';
      let errorVariant: 'destructive' | 'warning' = 'destructive';
      let errorTitle = 'Erro ao criar pedido';
      const rawMessage = error?.message || error?.error?.message || '';
      const pgCode = error?.code || error?.error?.code || '';
      
      if (rawMessage.includes('Renovação não virou pedido:')) {
        const motivo = rawMessage.split('Renovação não virou pedido:')[1]?.trim().replace(/[.;]+$/, '') || 'a proposta ainda não atende às regras de renovação';
        errorTitle = 'Renovação bloqueada';
        errorMessage = `O pedido não foi criado porque ${motivo}. Volte à proposta, corrija esses pontos e tente novamente.`;
        errorVariant = 'warning';
      } else if (rawMessage.includes('PEDIDO_SEM_ITENS')) {
        errorMessage = 'A proposta não gerou itens válidos. Verifique os dados da proposta.';
        errorVariant = 'warning';
      } else if (rawMessage.includes('violates row-level security') || pgCode === '42501') {
        errorMessage = 'Você não tem permissão para criar pedido com este perfil. Apenas consultores podem ter pedidos vinculados. Verifique com o gestor.';
        errorVariant = 'warning';
      } else if (rawMessage.includes('duplicate key') || pgCode === '23505') {
        errorMessage = 'Este pedido pode já ter sido criado. Verifique a lista de pedidos antes de tentar novamente.';
        errorVariant = 'warning';
      } else if (rawMessage.includes('violates foreign key') || pgCode === '23503') {
        errorMessage = 'Referência inválida: verifique se o consultor, empresa ou cliente selecionado existe no sistema.';
      } else if (rawMessage.includes('not-null') || pgCode === '23502') {
        errorMessage = 'Dado obrigatório ausente. Verifique se todos os campos foram preenchidos corretamente.';
      } else if (rawMessage) {
        errorMessage = rawMessage;
      }
      
      toast({
        title: errorVariant === 'warning' && errorTitle === 'Erro ao criar pedido' ? 'Ação não permitida' : errorTitle,
        description: errorMessage,
        variant: errorVariant,
      });
    } finally {
      setLoading(false);
      isCreatingRef.current = false; //  Reset do bloqueio
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Abrir Novo Pedido
          </DialogTitle>
        </DialogHeader>

        {/*  NOVO: Step para seleção de consultor (gestores/BKO) */}
        {step === 'consultor' && (
          <div className="space-y-4">
            <div className="bg-muted/50 p-3 rounded-lg border">
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" />
                Selecione o consultor responsável pelo pedido
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Consultor</Label>
              {isLoadingConsultores ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : (
                <Combobox
                  options={opcoesConsultores}
                  value={consultorSelecionadoId}
                  onValueChange={handleSelecionarConsultor}
                  placeholder="Buscar consultor..."
                  searchPlaceholder="Digite o nome..."
                  emptyMessage="Nenhum consultor encontrado"
                />
              )}
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {step === 'cnpj' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cnpj">CNPJ da Empresa</Label>
              <Input
                id="cnpj"
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={(e) => setCnpj(formatarCNPJ(e.target.value))}
                maxLength={18}
              />
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={() => buscarEmpresa()} disabled={loading || !validarCNPJ(cnpj)}>
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Buscar Empresa
              </Button>
            </div>
          </div>
        )}

        {step === 'empresa' && (
          <EmpresaManualForm 
            cnpj={cnpj}
            onSubmit={criarEmpresaManual}
            onCancel={() => setStep('cnpj')}
            loading={loading}
          />
        )}

        {step === 'pedido' && erroClientePf && <p role="alert">{erroClientePf}</p>}
        {step === 'pedido' && loading && !empresaSelecionada && <p>Carregando cliente CPF…</p>}
        {step === 'pedido' && empresaSelecionada && (
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold">{empresaSelecionada.razao_social}</h3>
              <p className="text-sm text-muted-foreground">
                {isPessoaFisica ? 'CPF' : 'CNPJ'}: {isPessoaFisica ? formatarCPF(empresaSelecionada.cpf) : formatarCNPJ(empresaSelecionada.cnpj)}
              </p>
              {empresaSelecionada.nome_fantasia && (
                <p className="text-sm text-muted-foreground">
                  Nome fantasia: {empresaSelecionada.nome_fantasia}
                </p>
              )}
            </div>

            {situacaoReceita && !/^ATIV/i.test(situacaoReceita.trim()) && (
              <div className="bg-destructive/10 p-4 rounded-lg border-2 border-dashed border-destructive/40">
                <p className="text-sm text-destructive flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Esta empresa está <strong>{situacaoReceita.trim().toUpperCase()}</strong> na Receita
                    Federal. Confirme a situação com o cliente antes de seguir — o pedido pode não ativar.
                  </span>
                </p>
              </div>
            )}

            <div className="bg-muted/50 p-4 rounded-lg border-2 border-dashed">
              <p className="text-sm text-muted-foreground text-center">
                 Após criar, o pedido será aberto e você poderá adicionar itens
              </p>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button 
                onClick={criarPedido} 
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Criar Pedido
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface EmpresaManualFormProps {
  cnpj: string;
  onSubmit: (dados: any) => void;
  onCancel: () => void;
  loading: boolean;
}

function EmpresaManualForm({ cnpj, onSubmit, onCancel, loading }: EmpresaManualFormProps) {
  const [formData, setFormData] = useState({
    razaoSocial: '',
    nomeFantasia: '',
    telefone: '',
    email: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-muted border p-3 rounded-lg">
        <p className="text-sm text-muted-foreground">
          Empresa não encontrada na Receita Federal. Preencha os dados básicos para continuar.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="razaoSocial">Razão Social *</Label>
        <Input
          id="razaoSocial"
          required
          value={formData.razaoSocial}
          onChange={(e) => setFormData(prev => ({ ...prev, razaoSocial: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="nomeFantasia">Nome Fantasia</Label>
        <Input
          id="nomeFantasia"
          value={formData.nomeFantasia}
          onChange={(e) => setFormData(prev => ({ ...prev, nomeFantasia: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="telefone">Telefone</Label>
        <Input
          id="telefone"
          value={formData.telefone}
          onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">E-mail</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
        />
      </div>
      
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Voltar
        </Button>
        <Button type="submit" disabled={loading || !formData.razaoSocial}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Criar Empresa
        </Button>
      </div>
    </form>
  );
}
